import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    Button,
    Layout,
    Menu,
    PageHeader,
    Avatar,
    Dropdown,
    Space,
    Modal,
} from 'antd';
import {
    HomeOutlined,
    UserOutlined,
    MenuOutlined,
    LogoutOutlined,
    BookOutlined,
    QuestionOutlined
} from '@ant-design/icons';
import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';
import {
    checkEnvironmentIsTao,
    generateTitle,
    getDefaultFeatures,
    generateUserDomain,
    generateApiDomain,
    getIsActiveTeacherOrAbove,
} from 'constants/GlobalConfig';
import Title from 'components/modules/Title';
import { BackgroundJobMenu } from '../../modules/Job/Background/BackgroundJobMenu';
import { BackgroundJobProvider } from '../../modules/Job/Background/BackgroundJobProvider';
import LeftSideMenu from 'components/modules/LeftSideMenu';
import { LicenseModal } from 'components/modules/LicenseModal';
import buildMetadata from '../../../metadata.json';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import { AuthenticatorModal } from '../pages/MFA/AuthenticatorModal';
import { useDisable2FA } from 'constants/CustomHooks/useDisable2FA';

const { Content, Footer, Sider, } = Layout;
const MENU_WIDTH_DEFAULT = 250;
const MENU_WIDTH_RESPONSIVE = 210;
const IPAD_BREAKPOINTS = 1024;

const BaseLayout = props => {
    const [, setFeatures] = useState(getDefaultFeatures());
    const dispatch = useDispatch();
    const isControllableUser = useSelector(state => state.isControllableUser);
    const manualGetConnection = useSelector(state => state.manualGetConnection);
    const location = useLocation();
    const [titleObject, setTitleObject] = useState(generateTitle(location.pathname));
    const user = useSelector(state => state.user);
    const tenant = useSelector(state => state.tenant);
    const [collapsed, setCollapsed] = useState(false)
    const [visible, setVisible] = useState(false);
    const isEnableMFA = useAppSelector(state => state.isEnableMFA.payload);
    const session = useAppSelector(state => state.session);
    const [menuWidth, setMenuWidth] = useState(MENU_WIDTH_DEFAULT);
    const [, requestDisable] = useDisable2FA(
        () => {
            setTimeout(() => {
                window.location.reload();
            }, 100);
        }
    );

    const buildInfo = [
        `Build.${buildMetadata.buildRevision}`,
        buildMetadata.buildDate,
    ].join(' ');

    useEffect(() => {
        setTitleObject(generateTitle(location.pathname));
        // ブラウザウィンドウのサイズと最大タッチポイント数の確認
        // このロジックは、PCデバイスとタッチスクリーンデバイス（タブレット/モバイル）を区別するために使用されます。
        if (window.innerWidth > IPAD_BREAKPOINTS && navigator.maxTouchPoints === 0) {
            document.querySelector('html').scrollTo(0, 0);
        } else {
            document.querySelector('html').scrollTo(5, 5);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (tenant.payload?.features) {
            setFeatures(tenant.payload.features);
        }
    }, [tenant]);

    useEffect(() => {
        if (manualGetConnection.payload?.result !== undefined) return;

        dispatch(Actions.http.connection.manual.get());
        const handleResize = () => {
            // iOS 26でサイドバーのバグを回避するために、120ミリ秒スリープする。
            setTimeout(() => {
                if (window.innerWidth <= IPAD_BREAKPOINTS) {
                    setMenuWidth(MENU_WIDTH_RESPONSIVE);
                    setCollapsed(true);
                } else {
                    setMenuWidth(MENU_WIDTH_DEFAULT);
                    setCollapsed(false);
                }
            }, 120);
        };

        handleResize();
    
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (session.payload.is_timeout) {
            // 学校管理画面に、現年度で教員以上の権限を持たないユーザー（児童生徒など）がサインインすると
            // 管理 API が権限エラー（403）になり続けるため、リロードしても同じダイアログに戻る。
            // その場合は利用者画面のサインインへ戻す（LGT-3888）
            const shouldGoUserSite = !isControllableUser.payload
                && !!user?.payload?.belongs
                && !getIsActiveTeacherOrAbove(user.payload);
            Modal.error({
                title: 'セッションはタイムアウトしました',
                content: '再度ログインしてください。',
                onOk: () => {
                    window.location.replace(shouldGoUserSite ? generateUserDomain() : './');
                },
            });
        }
        // user / isControllableUser はダイアログ表示時点の値を使えばよいので依存に含めない
    }, [session]);// eslint-disable-line react-hooks/exhaustive-deps

    const logout = () => {
        dispatch(Actions.http.connection.authentication.logout());
    };

    const goPortal = () => {
        window.location = generateUserDomain();
    };

    /**
     * 二要素認証を有効化する処理
     * 二要素認証のセットアップ画面を開く
     */
    const handleEnableAuthentication = () => {
        dispatch(Actions.http.connection.authentication.setup());
    };

    const handleDisableAuthentication = () => {
        if (isControllableUser.payload) {
            // 全体管理者は remove-input-code 画面が無いため、従来どおりAPIで無効化する
            requestDisable();
        } else {
            // 学校管理者は利用者画面と同様に、コード入力画面へ遷移して認証アプリの登録を削除する。
            // 削除後は元の管理画面へ戻すため redirect を渡す。
            const redirectQuery = `?redirect=${encodeURIComponent(window.location.href)}`;
            window.location.replace(`${generateApiDomain(true)}/two-factor-auth/remove-input-code${redirectQuery}`);
        }
    };

    const manualInfo = {
        isActive: manualGetConnection.payload.result?.is_active,
        label: manualGetConnection.payload.result?.label,
        url: manualGetConnection.payload.result?.url,
    };

    const menu = (
        isControllableUser.payload ?
            <Menu>
                {manualInfo.isActive &&
                    <Menu.Item
                        icon={<BookOutlined />}
                        onClick={() => { window.open(manualInfo.url, '_blank'); }}
                        style={{ padding: '16px' }}
                    >
                        <span>{manualInfo.label}</span>
                    </Menu.Item>
                }
                <Menu.Item
                    icon={<QuestionOutlined />}
                    onClick={() => { window.open(checkEnvironmentIsTao() ? 'https://support2.mexcbt.mext.go.jp/contact/' : 'https://www.info.l-gate.net/inquiry_mng/', '_blank'); }}
                    style={{ padding: '16px' }}
                >
                    <span>ヘルプデスク</span>
                </Menu.Item>
                {
                    // 全体管理者のMFA解除はMFA管理画面に一本化するため「認証アプリの無効化」は表示しない
                    isEnableMFA && !user.payload.registered_mfa &&
                        <Menu.Item
                            icon={
                                <FontAwesomeIcon
                                    className='size-6 mr-3 items-center fill-current'
                                    icon={faUserShield}
                                />
                            }
                            onClick={handleEnableAuthentication}
                            style={{ padding: '16px' }}
                        >
                            <span>認証アプリの登録</span>
                        </Menu.Item>
                }
                <Menu.Item
                    key='header_menu_1'
                    icon={<LogoutOutlined />}
                    danger
                    style={{ padding: '16px' }}
                    onClick={logout}
                >
                    サインアウト
                </Menu.Item>
            </Menu>
            :
            <Menu>
                <Menu.Item
                    key='header_menu_1'
                    icon={<HomeOutlined />}
                    onClick={goPortal}
                    style={{ padding: '16px' }}
                    disabled={isControllableUser.payload}
                >
                    ユーザーホーム画面
                </Menu.Item>
                {manualInfo.isActive &&
                    <Menu.Item
                        icon={<BookOutlined />}
                        onClick={() => { window.open(manualInfo.url, '_blank'); }}
                        style={{ padding: '16px' }}
                    >
                        <span>{manualInfo.label}</span>
                    </Menu.Item>
                }
                <Menu.Item
                    icon={<QuestionOutlined />}
                    onClick={() => { window.open(checkEnvironmentIsTao() ? 'https://support2.mexcbt.mext.go.jp/contact/' : 'https://www.info.l-gate.net/inquiry_mng/', '_blank'); }}
                    style={{ padding: '16px' }}
                >
                    <span>ヘルプデスク</span>
                </Menu.Item>
                <Menu.Item key='headeR_menu_2'
                    icon={<LogoutOutlined />}
                    danger
                    onClick={logout}
                    style={{ padding: '16px' }}
                >
                    <span>サインアウト</span>
                </Menu.Item>
            </Menu>
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Title text={titleObject.title} />
            <Sider
                style={{
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                }}
                collapsedWidth='0'
                collapsed={collapsed}
                collapsible={window.innerWidth <= IPAD_BREAKPOINTS}
                onCollapse={() => {
                    setCollapsed(!collapsed);
                }}
                className='global-nav'
                width={menuWidth}>
                <div
                    className={checkEnvironmentIsTao() ? 'logo-m' : 'logo'}
                    style={{ backgroundSize: 'contain' }}
                />

                <LeftSideMenu selectedKey={location.pathname} />

                {/*バックグラウンドジョブ*/}
                <BackgroundJobProvider>
                    <BackgroundJobMenu collapsed={collapsed} />
                </BackgroundJobProvider>

            </Sider>
            <Layout className='container-outer'>
                <PageHeader
                    style={{
                        padding: '0 24',
                        background: 'white',
                        border: '1px solid rgb(235, 237, 240)',
                    }}
                    title={titleObject.title}
                    subTitle={titleObject.subTitle}
                    extra={[
                        <Dropdown key='dropdownExtra' overlay={menu} placement='topRight' arrow trigger={['click']}>
                            <Space size={15} className='header_prof'>
                                <Avatar key='avator'
                                    style={isControllableUser.payload ? { backgroundColor: '#f50' } : { backgroundColor: '#87d068' }}
                                    size={44} icon={<UserOutlined />}
                                />
                                <div>
                                    <span key='tenant'
                                        className='header_label_tenant'
                                    >
                                        {tenant ? tenant.payload?.name : ''}
                                    </span>
                                    <span key='organization'
                                        className='header_label_organization'
                                    >
                                        {isControllableUser.payload ? '全体管理者' : `${user.payload?.organization?.name}`}
                                    </span><br />
                                    <span key='display_name'
                                        className='header_label_name'
                                    >
                                        {user ? `${user.payload?.user_name} さん` : ''}
                                    </span><br />
                                </div>
                                <MenuOutlined className='header_prof_icon' />
                            </Space>
                        </Dropdown>,
                    ]}
                />
                <Content style={{ margin: '24px 16px 0', overflow: 'initial' }}>
                    {props.children}
                </Content>
                <Footer style={{ textAlign: 'left' }}>
                    {checkEnvironmentIsTao() ? '' : 'L-Gate / '}WebAdmin {buildInfo}<br />
                    COPYRIGHT(C) UCHIDA YOKO CO., LTD. ALL RIGHTS RESERVED. | <Button type='link' onClick={() => setVisible(true)}>ライセンス情報</Button>
                </Footer>
            </Layout>

            <LicenseModal
                visible={visible}
                onClose={() => setVisible(false)}
            />

            <AuthenticatorModal />
        </Layout>
    );
};

export default BaseLayout;
